import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import {
  ChevronLeft,
  ChevronRight,
  Info,
  Loader2,
  Lock,
  MessageSquare,
  UserPlus,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import {
  useLeaveReachOut,
  useNurtureList,
  usePullInNurture,
} from '../../../services/onboardingBoard';
import { ReachOutDialog, ReachOutLine, ReengagedBadge } from './reachOut';
import { VIEW_ONLY_TITLE } from './ownership';
import ReengagePicker, { reengageReady } from '../workspace/ReengagePicker';

// Nurture (Nic 2026-09-28, moved off Lead Handoffs): a paginated list, not
// tiles, because a lot of leads end up here. Nurture is for people who are
// not ready, so reps can focus on who is. Re-engage (Nic 2026-10-06) works
// on anyone, any time: it puts them in YOUR Signing Up, Book or sign up,
// to book a follow-up or send the agreement that day, or they come back
// here tonight. Anyone who reaches out while parked (a text, a call, a
// booking, a form again; lib/leadReachOut.js) jumps to the top with a
// "Reached out" flag and what came in; "Leave in Nurture" answers it
// without re-engaging (Nic 2026-10-15).
//
// Premium pass (Nic 2026-10-08): real columns under one header, the reason
// as the readable headline, filter chips by why they're here (server-side,
// with counts), and Mine / Everyone. Pagination and search stay on the
// server: one page of 50 per request.

// Why they're here, in the server's NURTURE_CATEGORIES order. dot = the
// small color mark on the chip and the row.
const CATEGORIES = [
  { key: 'no_follow_up', label: "Didn't book", dot: 'bg-amber-400' },
  { key: 'missed', label: 'No-show', dot: 'bg-rose-400' },
  { key: 'met', label: "Met, didn't sign", dot: 'bg-indigo-400' },
  { key: 'signup', label: 'Signup stalled', dot: 'bg-sky-400' },
  { key: 'never_reached', label: 'Never reached', dot: 'bg-slate-400' },
  { key: 'rep', label: 'Moved by a rep', dot: 'bg-violet-400' },
];
const CATEGORY_BY_KEY = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

export const ago = (at) => (at ? moment(at).fromNow() : null);
export const initialsOf = (name) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('') || '?';
const firstNameOf = (name) => String(name || '').split(/\s+/)[0] || null;

// The headline (why they're here, in plain words) and the small tag that
// says who decided it.
const reasonOf = (r) => {
  if (r.category !== 'rep') {
    return {
      text:
        r.reasonDetail ||
        (r.category === 'never_reached'
          ? 'Not reached during the 10-day cadence'
          : r.reason || 'Moved to Nurture'),
      tag: r.via === 'handoffs_cutover' ? 'At launch' : 'Auto',
    };
  }
  return {
    text: (r.reason === 'Other' && r.reasonDetail) || r.reason || 'Moved to Nurture',
    tag: r.movedBy ? `By ${firstNameOf(r.movedBy)}` : 'By a rep',
  };
};

// lg+ is a table; below it each row folds into a small card.
const GRID =
  'lg:grid lg:grid-cols-[minmax(0,1.25fr)_7rem_minmax(0,2fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_7rem] lg:items-center lg:gap-x-6';
// Header, rows and section labels share one side padding. Pixel values on
// purpose: px-4 / px-5 are also Bootstrap classes (24px / 48px, !important)
// and pushed the header 24px off its columns (Nic 2026-10-08).
export const PAD_X = 'px-[16px] lg:px-[20px]';

export const Chip = ({ active, onClick, dot, label, count, tone = 'slate' }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold outline-none focus:outline-none',
      active
        ? tone === 'sky'
          ? 'bg-sky-600 text-white dark:!bg-sky-500'
          : 'bg-slate-900 text-white dark:!bg-slate-100 dark:!text-slate-900'
        : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/10',
    )}
  >
    {dot ? <span className={cn('size-1.5 rounded-full', dot)} /> : null}
    {label}
    <span className={cn('tabular-nums', active ? 'opacity-70' : 'text-slate-400 dark:text-slate-500')}>
      {count}
    </span>
  </button>
);

const Row = ({ r, onReengage, onLeave, busy, flash, canWork, viewOnly = false }) => {
  const reason = reasonOf(r);
  const cat = CATEGORY_BY_KEY[r.category];
  const nurtured = r.nurturedAt ? moment(r.nurturedAt) : null;
  const ref = useRef(null);
  // Picked from the people search: bring the row into view.
  useEffect(() => {
    if (flash) ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [flash]);
  return (
    <li
      ref={ref}
      className={cn(
        'group flex flex-col gap-2 py-3 hover:bg-slate-50/80 dark:hover:!bg-white/[0.03]',
        PAD_X,
        GRID,
        r.detectedAt &&
          'bg-sky-50/70 shadow-[inset_3px_0_0_#0ea5e9] hover:bg-sky-50 dark:!bg-sky-500/10 dark:hover:!bg-sky-500/15',
        flash &&
          '!bg-amber-50 shadow-[inset_0_0_0_2px_#f59e0b] dark:!bg-amber-400/10 dark:shadow-[inset_0_0_0_2px_#fbbf24]',
      )}
    >
      {/* Lead */}
      <div className="flex min-w-0 items-center gap-3">
        <span
          aria-hidden
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
            r.detectedAt
              ? 'bg-sky-600 text-white'
              : 'bg-slate-100 text-slate-600 dark:!bg-white/10 dark:text-slate-300',
          )}
        >
          {initialsOf(r.name)}
        </span>
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
              {r.name}
            </span>
          </div>
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
            {r.detectedAt ? (
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-sky-600 px-1.5 py-px text-[10.5px] font-semibold text-white">
                <MessageSquare size={10} /> Reached out {ago(r.detectedAt)}
              </span>
            ) : r.sourceLabel ? (
              <span className="truncate text-xs text-slate-400 dark:text-slate-500">
                {r.sourceLabel}
              </span>
            ) : null}
          </div>
          {/* What they did (2026-10-15): the text itself, a call, a booking,
              a form again. */}
          {r.detectedAt ? <ReachOutLine reachOut={r.reachOut} /> : null}
        </div>
      </div>

      {/* Times re-engaged, from Nurture or Dropped (Nic 2026-10-08). */}
      <div className="hidden justify-center lg:flex">
        <ReengagedBadge r={r} />
      </div>

      {/* Why they're here */}
      <div className="min-w-0 pl-11 lg:pl-0">
        <div
          className="line-clamp-2 text-[13px] leading-snug text-slate-700 dark:text-slate-200"
          title={reason.text}
        >
          {reason.text}
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400 dark:text-slate-500">
          {cat ? <span className={cn('size-1.5 shrink-0 rounded-full', cat.dot)} /> : null}
          <span>{cat?.label || 'Nurture'}</span>
          <span aria-hidden>·</span>
          <span>{reason.tag}</span>
        </div>
      </div>

      {/* Nurtured: when. On small screens the owner rides along here. */}
      <div className="flex min-w-0 items-baseline gap-2 pl-11 lg:block lg:pl-0">
        <div className="whitespace-nowrap text-[13px] text-slate-700 dark:text-slate-200">
          {nurtured ? nurtured.format('MMM D') : '-'}
          {nurtured ? (
            <span className="text-slate-400 dark:text-slate-500"> · {nurtured.fromNow()}</span>
          ) : null}
        </div>
        <div className="truncate text-[11px] text-slate-400 dark:text-slate-500 lg:hidden">
          {r.isMine ? 'Your lead' : r.ownerName ? `${r.ownerName}'s lead` : 'No owner'}
        </div>
      </div>

      {/* Owner: whose lead it was when it left the board (Nic 2026-10-08),
          its own column so it never reads as who moved them. */}
      <div className="hidden min-w-0 items-center gap-2 lg:flex">
        {r.ownerName ? (
          <>
            <span
              aria-hidden
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                r.isMine
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-600 dark:!bg-white/10 dark:text-slate-300',
              )}
            >
              {initialsOf(r.ownerName)}
            </span>
            <span
              className="truncate text-[13px] text-slate-700 dark:text-slate-200"
              title={r.ownerName}
            >
              {r.isMine ? 'You' : r.ownerName}
            </span>
          </>
        ) : (
          <span className="text-[13px] italic text-slate-400 dark:text-slate-500">No owner</span>
        )}
      </div>

      {/* Last touch */}
      <div className="hidden min-w-0 lg:block">
        {r.lastTouchAt ? (
          <div
            className="truncate text-[13px] text-slate-600 dark:text-slate-300"
            title={moment(r.lastTouchAt).format('ddd MMM D, h:mm A')}
          >
            {ago(r.lastTouchAt)}
          </div>
        ) : (
          <div className="text-[13px] italic text-slate-400 dark:text-slate-500">Never</div>
        )}
      </div>

      {/* One fixed slot so the column lines up. A reached-out row also gets
          "Leave in Nurture" under Re-engage (Nic 2026-10-15): answers the
          reach-out and drops the row back into the list. */}
      <div className="flex flex-col items-start gap-1 pl-11 lg:items-end lg:pl-0">
        {/* Only for people who work the board (QA 2026-10-13): view only
            gets a 403 from pull-in. */}
        {/* Admins and viewers see them greyed (Nic 2026-10-23). */}
        {r.canPullIn && (canWork || viewOnly) ? (
          <button
            type="button"
            disabled={busy || viewOnly}
            onClick={() => onReengage(r)}
            title={
              viewOnly
                ? VIEW_ONLY_TITLE
                : 'Puts them on your Signing Up, under Follow-up > Book or sign up: book a follow-up or send the agreement today'
            }
            className={cn(
              'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border-[1px] px-3 text-xs font-semibold outline-none focus:outline-none disabled:opacity-50',
              // Important fills: a plain hover:bg lost to the theme's button
              // background in light mode (white text on white, Nic 2026-10-08).
              'border-slate-200 !bg-white !text-slate-700 hover:!border-slate-900 hover:!bg-slate-900 hover:!text-white group-hover:!border-slate-900 group-hover:!bg-slate-900 group-hover:!text-white',
              'dark:!border-white/15 dark:!bg-transparent dark:!text-slate-200 dark:hover:!border-slate-100 dark:hover:!bg-slate-100 dark:hover:!text-slate-900 dark:group-hover:!border-slate-100 dark:group-hover:!bg-slate-100 dark:group-hover:!text-slate-900',
            )}
          >
            <UserPlus size={13} /> Re-engage
          </button>
        ) : null}
        {r.detectedAt && (canWork || viewOnly) ? (
          <button
            type="button"
            disabled={busy || viewOnly}
            title={viewOnly ? VIEW_ONLY_TITLE : undefined}
            onClick={() => onLeave(r)}
            className="whitespace-nowrap px-1 text-[11px] font-semibold text-slate-400 outline-none hover:text-slate-700 hover:underline focus:outline-none disabled:opacity-50 dark:text-slate-500 dark:hover:text-slate-200"
          >
            Leave in Nurture
          </button>
        ) : null}
      </div>
    </li>
  );
};

// A small label between the reached-out leads and everyone else.
export const SectionLabel = ({ children, tone = 'slate' }) => (
  <li
    className={cn(
      'pb-1.5 pt-3 text-[10.5px] font-bold uppercase tracking-wider',
      PAD_X,
      tone === 'sky' ? 'text-sky-600 dark:text-sky-400' : 'text-slate-400 dark:text-slate-500',
    )}
  >
    {children}
  </li>
);

// q is the board's one search (the header box), already debounced. focus
// ({ leadId, n }) comes from clicking a Nurture result in that search: the
// chips reset to All / Everyone so the lead can't hide behind a filter,
// and its row is ringed for a few seconds (Nic 2026-10-08).
// viewer / reps (Nic 2026-10-23): the manager picks who gets a re-engage
// from reps; admins and viewers see it all greyed.
const NurtureView = ({ q = '', focus = null, onPulledIn, canWork = false, keep = null, viewer = null, reps = [] }) => {
  const viewOnly = viewer?.canAct === false;
  const [picking, setPicking] = useState(null);
  const [pickTo, setPickTo] = useState('');
  // Page and chips (Nic 2026-10-20): the board keeps them (`keep`), so a tab
  // only on screen while open comes back the way it was left. Standalone, it
  // keeps them itself.
  const [localKeep, setLocalKeep] = useState({ page: 1, reason: '', mine: false, q: '' });
  const kept = keep ? keep.state : localKeep;
  const setKept = keep ? keep.setState : setLocalKeep;
  const { page, reason, mine } = kept;
  const setPage = (v) =>
    setKept((s) => ({ ...s, page: typeof v === 'function' ? v(s.page) : v }));
  const setReason = (v) => setKept((s) => ({ ...s, reason: v }));
  const setMine = (v) => setKept((s) => ({ ...s, mine: v }));
  const [flashId, setFlashId] = useState(null);
  const list = useNurtureList({ page, q, reason, mine });
  const pull = usePullInNurture();
  const leave = useLeaveReachOut();
  const [leaving, setLeaving] = useState(null);
  const leaveIt = async (note) => {
    if (!leaving) return;
    try {
      await leave.mutateAsync({ leadId: leaving.leadId, as: 'nurture', note });
      toast.success(`${leaving.name} stays in Nurture.`);
      setLeaving(null);
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't go through.");
    }
  };

  // A new search or filter starts at page 1; coming back to the tab with the
  // same search keeps the page. Compared with the last values seen, not a
  // first-run flag (branch QA 2026-10-21: React's dev double-run reset the
  // page every time).
  const lastFilters = useRef({ q: kept.q, reason, mine });
  useEffect(() => {
    const was = lastFilters.current;
    if (was.q === q && was.reason === reason && was.mine === mine) return;
    lastFilters.current = { q, reason, mine };
    setKept((s) => ({ ...s, page: 1, q }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, reason, mine]);

  useEffect(() => {
    if (!focus?.leadId) return undefined;
    setReason('');
    setMine(false);
    setFlashId(focus.leadId);
    const t = setTimeout(() => setFlashId(null), 4000);
    return () => clearTimeout(t);
    // Only a new pick from the search runs this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  const rows = list.data?.rows || [];
  const total = list.data?.total || 0;
  const counts = list.data?.counts || {};
  const pageSize = list.data?.pageSize || 50;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(total, page * pageSize);

  const reached = rows.filter((r) => r.detectedAt);
  const rest = rows.filter((r) => !r.detectedAt);
  const split = reached.length > 0 && rest.length > 0;

  const pullIn = async (row, toUserId = null) => {
    try {
      await pull.mutateAsync({ leadId: row.leadId, toUserId });
      const to = toUserId ? reps.find((p) => p.userId === toUserId) : null;
      toast.success(
        to && toUserId !== viewer?.userId
          ? `${row.name} is on ${to.name}'s Signing Up, under Book or sign up.`
          : `${row.name} is on your Signing Up, under Book or sign up. Book a follow-up or send the agreement today, or they come back here tonight.`,
      );
      setPicking(null);
      onPulledIn?.(row.leadId);
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That lead couldn't be re-engaged.");
    }
  };

  // The manager picks who gets them first; a rep gets them in one click.
  const startPullIn = (row) => {
    if (viewer?.isManager) {
      setPickTo('');
      setPicking(row);
      return;
    }
    pullIn(row);
  };

  // Chips with nobody in them stay out of the way, unless picked.
  const chips = CATEGORIES.filter((c) => counts[c.key] > 0 || reason === c.key);
  const filterLabel = reason === 'reached_out' ? 'Reached out' : CATEGORY_BY_KEY[reason]?.label;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Filters */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="os-scroll -mx-1 flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto px-1 py-0.5">
          <Chip
            active={!reason}
            onClick={() => setReason('')}
            label="All"
            count={counts.all ?? total}
          />
          {counts.reached_out > 0 || reason === 'reached_out' ? (
            <Chip
              active={reason === 'reached_out'}
              onClick={() => setReason('reached_out')}
              label="Reached out"
              count={counts.reached_out || 0}
              dot="bg-sky-500"
              tone="sky"
            />
          ) : null}
          {chips.map((c) => (
            <Chip
              key={c.key}
              active={reason === c.key}
              onClick={() => setReason(reason === c.key ? '' : c.key)}
              label={c.label}
              count={counts[c.key] || 0}
              dot={c.dot}
            />
          ))}
        </div>
        <div
          role="group"
          aria-label="Whose leads"
          className="flex shrink-0 items-center rounded-full bg-slate-200/70 p-0.5 dark:!bg-white/10"
        >
          {[
            { v: false, label: 'Everyone' },
            { v: true, label: 'Mine' },
          ].map((o) => (
            <button
              key={o.label}
              type="button"
              aria-pressed={mine === o.v}
              onClick={() => setMine(o.v)}
              className={cn(
                'h-6 rounded-full px-3 text-xs font-semibold outline-none focus:outline-none',
                mine === o.v
                  ? 'bg-white text-slate-800 shadow-sm dark:!bg-slate-100 dark:!text-slate-900'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* The rule, and paging */}
      <div className="mb-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <Info size={13} className="shrink-0 text-slate-400" />
          Re-engage only to book or sign them today. If not, they come back here tonight.
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Lock size={12} className="shrink-0 text-slate-400" />
          Contact details stay hidden until you re-engage.
        </span>
        <div className="ml-auto flex items-center gap-1">
          <span className="whitespace-nowrap tabular-nums">
            {total ? (
              <>
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {from}-{to}
                </span>{' '}
                of {total}
              </>
            ) : (
              '0'
            )}
          </span>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
            className="rounded-lg p-1 text-slate-500 outline-none hover:bg-slate-200/60 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            aria-label="Next page"
            className="rounded-lg p-1 text-slate-500 outline-none hover:bg-slate-200/60 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="os-scroll relative min-h-0 flex-1 overflow-y-auto rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10">
        {/* Column header, lg+ only (below it rows are cards). */}
        <div
          className={cn(
            'sticky top-0 z-10 hidden border-b-[1px] border-slate-100 bg-white/95 py-2 text-[10.5px] font-bold uppercase tracking-wider text-slate-400 backdrop-blur dark:!border-white/10 dark:!bg-surface-card/95 dark:text-slate-500 lg:grid',
            PAD_X,
            GRID,
          )}
        >
          <div>Lead</div>
          <div className="text-center">Re-engaged</div>
          <div>Why they&apos;re here</div>
          <div>Nurtured</div>
          <div>Owner</div>
          <div>Last touch</div>
          <div className="text-right">
            {list.isFetching && !list.isLoading ? (
              <Loader2 size={12} className="ml-auto animate-spin" />
            ) : null}
          </div>
        </div>

        {list.isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 size={20} className="animate-spin text-slate-400" />
          </div>
        ) : list.isError ? (
          <div className="flex h-40 items-center justify-center text-sm font-semibold text-rose-500">
            {list.error?.response?.data?.msg || "Nurture couldn't be loaded."}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-40 flex-col items-center justify-center gap-1 text-sm text-slate-400">
            <div className="font-semibold text-slate-500 dark:text-slate-400">
              {q ? (
                <span className="font-medium italic text-sky-700 dark:text-sky-300">No matches for this search</span>
              ) : reason || mine ? (
                'Nobody matches.'
              ) : (
                'Nobody in Nurture.'
              )}
            </div>
            {reason || mine ? (
              <button
                type="button"
                onClick={() => {
                  setReason('');
                  setMine(false);
                }}
                className="text-xs font-semibold text-sky-600 outline-none hover:underline focus:outline-none dark:text-sky-400"
              >
                Clear filters
              </button>
            ) : null}
          </div>
        ) : (
          <ul
            aria-label={filterLabel ? `Nurture: ${filterLabel}` : 'Nurture'}
            className={cn(
              'mb-0 list-none divide-y divide-slate-100 pl-0 dark:divide-white/[0.06]',
              list.isFetching && !list.isLoading && 'opacity-70',
            )}
          >
            {split ? <SectionLabel tone="sky">Reached out</SectionLabel> : null}
            {reached.map((r) => (
              <Row
                key={r.leadId}
                r={r}
                onReengage={startPullIn}
                onLeave={setLeaving}
                busy={pull.isLoading || leave.isLoading}
                flash={flashId === r.leadId}
                canWork={canWork}
                viewOnly={viewOnly}
              />
            ))}
            {split ? <SectionLabel>Everyone else</SectionLabel> : null}
            {rest.map((r) => (
              <Row
                key={r.leadId}
                r={r}
                onReengage={startPullIn}
                onLeave={setLeaving}
                busy={pull.isLoading || leave.isLoading}
                flash={flashId === r.leadId}
                canWork={canWork}
                viewOnly={viewOnly}
              />
            ))}
          </ul>
        )}
      </div>
      <ReachOutDialog
        open={!!picking}
        title={picking ? `Re-engage ${picking.firstName || picking.name}?` : ''}
        confirmLabel="Re-engage"
        busy={pull.isLoading}
        confirmDisabled={!reengageReady(viewer, pickTo)}
        onConfirm={() => pullIn(picking, pickTo)}
        onClose={() => setPicking(null)}
      >
        Who gets them? They go on that rep&apos;s Signing Up, under Book or sign up.
        <ReengagePicker
          viewer={viewer}
          value={pickTo}
          onChange={setPickTo}
          options={reps.map((p) => ({ value: p.userId, label: p.name }))}
          className="mt-3 [&>*]:w-full"
        />
      </ReachOutDialog>
      <ReachOutDialog
        open={!!leaving}
        title={leaving ? `Leave ${leaving.firstName || leaving.name} in Nurture?` : ''}
        noteLabel="Note for their timeline (optional)"
        notePlaceholder="e.g. Asked a question, not ready yet"
        confirmLabel="Leave in Nurture"
        busy={leave.isLoading}
        onConfirm={leaveIt}
        onClose={() => setLeaving(null)}
      >
        Their reach-out is answered and they move back into the list. You can
        still re-engage them any time.
      </ReachOutDialog>
    </div>
  );
};

export default NurtureView;
